import { FormAutosuggest, FormDateSuggest, FormPlaceSuggest, useWatch, useZionFormContext } from '@fs/zion-form'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSlsStateContext, getNestedOrFloatingElementById } from '@fs/zion-sls'
import { Cell, FlowGrid, IconButton, LabelSpacer, Row, useOverlay } from '@fs/zion-ui'
import { ContentDelete } from '@fs/zion-icon'
import FieldsSection from './FieldsSection'
import ConfirmUnlinkEvent from './ConfirmUnlinkEvent'
import LinkNamesButton from '../../panels/shared/LinkNamesButton'
import { useLinkNamesSave } from '../../panels/linkNamesPanel/hooks/useLinkNamesSave'
import { createEmptyEventWithType, parseEventToSimplifiedForm } from '../utils/eventsParsing.utils'
import RelationshipEventFields from './RelationshipEventFields'
import OtherEventFields from './OtherEventFields'

const EventForm = ({ event, index, formArrayMethods, eventAutosuggestOptions, person, splitScreenImageArk }) => {
  const [t] = useTranslation()
  const slsState = useSlsStateContext()
  const { trigger, getValues, setValue, control, resetField } = useZionFormContext()
  const confirmUnlinkOverlay = useOverlay()
  const [personToUnlink, setPersonToUnlink] = useState(null)

  const fieldNamePrefix = event?.primary ? 'events.primary' : 'events.additional'
  const eventTypeFieldName = `${fieldNamePrefix}[${index}].eventType`
  const basePath = `${fieldNamePrefix}[${index}]`

  // Watch for existing event ID and linkedPersonIds from form data
  const existingEventId = useWatch({ control, name: `${basePath}.existingEventId` })
  const linkedPersonIds = useWatch({ control, name: `${basePath}.linkedPersonIds` })
  const eventTypeField = useWatch({ name: eventTypeFieldName })
  const showRelationshipFields = eventTypeField?.isRelationshipEvent
  const isPersistedRelationshipEvent = event?.relationship?.id || event?.relationshipId

  // Watch the actual form values for dates, places, and other to ensure we render current state
  const watchedDates = useWatch({ name: `${basePath}.dates` }) || event.dates
  const watchedPlaces = useWatch({ name: `${basePath}.places` }) || event.places
  const watchedOther = useWatch({ name: `${basePath}.other` }) || event.other

  // Check if this is a linked event (has linkedPersonIds with more than 1 person)
  const isLinkedEvent = linkedPersonIds?.length > 1

  // Derive the selected event based on existingEventId
  const selectedEvent = useMemo(() => {
    if (!existingEventId) return null

    // Try to find the event in the SLS state
    return getNestedOrFloatingElementById(slsState.images, existingEventId)
  }, [existingEventId, slsState.images])

  // Use the hook for handling link names save
  const { handleLinkNamesSave } = useLinkNamesSave({
    getValues,
    images: slsState.images,
    currentImageArk: slsState.currentImageArk,
    updateField: formArrayMethods.update,
    basePath: fieldNamePrefix,
  })

  // Callback to trigger validation on both date and place fields when either field blurs
  const handleValidateEventFields = useCallback(async () => {
    // Build paths for all date and place sub-element fields in THIS event
    const datePaths = (watchedDates || []).map(
      (dateItem, dateIndex) => `${fieldNamePrefix}[${index}].dates[${dateIndex}].value`
    )
    const placePaths = (watchedPlaces || []).map(
      (placeItem, placeIndex) => `${fieldNamePrefix}[${index}].places[${placeIndex}].value`
    )

    // Trigger validation on all date and place fields
    // This ensures errors appear on both fields when both are empty
    const allPaths = [...datePaths, ...placePaths]
    if (allPaths.length > 0) {
      await trigger(allPaths, { shouldFocus: false })
    }
  }, [fieldNamePrefix, index, watchedDates, watchedPlaces, trigger])

  return (
    <>
      <FlowGrid
        orientation="vertical"
        columnCount={1}
        gutters="nano"
        key={`event-${event.id}-${existingEventId || 'new'}`}
      >
        <Cell>
          <Row gutters="xxs" alignItemsY="middle">
            {/* Event type autosuggest */}
            <FormAutosuggest
              name={eventTypeFieldName}
              label={t('edit-events.fields.event-type', 'Event Type')}
              data-testid="event-type-autosuggest"
              suggestions={eventAutosuggestOptions}
              onSuggestionSelected={(selectedSuggestion) => {
                // Check if an existing event was selected
                if (selectedSuggestion?.isExistingEvent && selectedSuggestion?.existingEvent) {
                  const existingEvent = selectedSuggestion.existingEvent

                  // Populate form values from the existing event
                  const newEventData = parseEventToSimplifiedForm(
                    existingEvent,
                    slsState.types,
                    person,
                    slsState.images
                  )

                  const newEventDataWithUpdatedLinkedPersonIds = {
                    ...newEventData,
                    primary: event.primary, // Preserve whether this is primary or additional
                    added: true, // Mark as added to process it as a new event, we do more checks on save to link this event
                  }

                  // Reset field with new event data. This both updates the form values and clears
                  // dirty/touched state to prevent warning messages from persisting between event switches.
                  formArrayMethods.update(index, newEventDataWithUpdatedLinkedPersonIds)
                  resetField(basePath, { defaultValue: newEventDataWithUpdatedLinkedPersonIds })
                } else {
                  // Standard event type selected (new event)
                  // Start with a clean empty event structure to avoid ghost fields from previous selections
                  const emptyEventData = createEmptyEventWithType(
                    selectedSuggestion?.key,
                    slsState.types,
                    person,
                    event.primary
                  )

                  // Reset field with empty event structure. This both updates the form values and
                  // clears dirty/touched state to prevent warning messages from persisting.
                  formArrayMethods.update(index, emptyEventData)
                  resetField(basePath, { defaultValue: emptyEventData })
                }
              }}
              clearOnClickAway
              clearable
              growX
              dense
              // Disable to prevent editing of persisted relationship events. We'll look at a better UX later.
              // This is current behavior from RII.
              disabled={isPersistedRelationshipEvent}
              // Virtualization is disabled due to issues with grouped dropdown rendering.
            />
            {!event.primary && (
              <Cell>
                <LabelSpacer />
                <IconButton
                  Icon={ContentDelete}
                  title={t('edit-events.remove-event', 'Remove Event')}
                  onClick={() => {
                    if (isLinkedEvent) {
                      setPersonToUnlink(person)
                      confirmUnlinkOverlay.open()
                      return
                    }
                    formArrayMethods.remove(index)
                    trigger() // Re-run validation after removal
                  }}
                  color="danger"
                  data-testid={`unlink-event-button-${index}`}
                  linkName="Events Card Unlink Event"
                />
              </Cell>
            )}
          </Row>
          {/* Show View Linked Names button for events with multiple linked persons */}
          {event?.shared && !event?.added && (
            <LinkNamesButton
              label={t('edit-events.view-linked-names', 'View linked names to event')}
              person={person}
              field={event}
              onSave={handleLinkNamesSave(index)}
              linkedPersonIds={linkedPersonIds}
              disabled={event?.added}
              description={t(
                'shared-event.panel-description',
                'Do more names need to be linked to this {eventType} event?',
                {
                  eventType: event?.eventType?.primaryText,
                }
              )}
            />
          )}
        </Cell>

        {/* Date Fields */}
        <FieldsSection
          path={`${basePath}.dates`}
          indexName="dates"
          eventIndex={index}
          SuggestComponent={FormDateSuggest}
          suggestFieldType="DATE"
          suggestLabelKey="edit-events.fields.date"
          suggestDefaultLabel="Date"
          isLinkedEvent={isLinkedEvent}
          primaryEvent={event.primary}
          splitScreenImageArk={splitScreenImageArk}
          onValidateFields={handleValidateEventFields}
        />

        {/* Place Fields */}
        <FieldsSection
          path={`${basePath}.places`}
          indexName="places"
          eventIndex={index}
          SuggestComponent={FormPlaceSuggest}
          suggestFieldType="PLACE"
          suggestLabelKey="edit-events.fields.place"
          suggestDefaultLabel="Place"
          isLinkedEvent={isLinkedEvent}
          primaryEvent={event.primary}
          splitScreenImageArk={splitScreenImageArk}
          onValidateFields={handleValidateEventFields}
        />

        {watchedOther?.map((other, otherIndex) => (
          <OtherEventFields
            key={`other-${other.id}`}
            otherField={other}
            itemIndex={otherIndex}
            eventIndex={index}
            primaryEvent={event?.primary}
            isLinkedEvent={isLinkedEvent}
            splitScreenImageArk={splitScreenImageArk}
          />
        ))}

        {showRelationshipFields && (
          <RelationshipEventFields person={person} index={index} fieldNamePrefix={fieldNamePrefix} />
        )}
      </FlowGrid>
      {/* Confirm Unlink Dialog */}
      <ConfirmUnlinkEvent
        overlay={confirmUnlinkOverlay}
        person={personToUnlink}
        event={selectedEvent}
        onRemove={() => {
          if (!personToUnlink || !selectedEvent) return

          // Close dialog immediately (consistent with ConfirmUnlinkName pattern)
          confirmUnlinkOverlay.close()

          // If personToUnlink is the focus person, remove the entire event
          // Otherwise, just unlink that specific person from the event
          if (personToUnlink?.id === person?.id) {
            // Remove the entire event from the form
            formArrayMethods.remove(index)
            trigger() // Re-run validation after removal
            setPersonToUnlink(null)
          } else {
            // Update form state to remove the person from linkedPersonIds (visual feedback)
            const currentLinkedPersonIds = linkedPersonIds || []
            const updatedLinkedPersonIds = currentLinkedPersonIds.filter(
              (linkedPerson) => linkedPerson?.id !== personToUnlink?.id
            )
            setValue(`${basePath}.linkedPersonIds`, updatedLinkedPersonIds, { shouldDirty: true })
            setPersonToUnlink(null)
          }
        }}
      />
    </>
  )
}

export default EventForm
